import type { ContentTaxonomyChunksResponse } from "../../services/api";

export type TopicHierarchySubtopic = {
  slug: string;
  display: string;
  mentions: number;
};

export type TopicHierarchyNode = {
  slug: string;
  display: string;
  mentions: number;
  subtopics: TopicHierarchySubtopic[];
};

export type TreemapDatum = {
  name: string;
  slug: string;
  display: string;
  size: number;
  kind: "root" | "topic" | "subtopic";
  parentTopicSlug?: string;
  parentTopicDisplay?: string;
  fill?: string;
  children?: TreemapDatum[];
};

export type NetworkNode = {
  id: string;
  slug: string;
  display: string;
  mentions: number;
  kind: "topic" | "subtopic";
  parentTopicSlug?: string;
  colorIndex: number;
};

export type DominanceLevel = "topic" | "subtopic";

export type NetworkLink = {
  source: string;
  target: string;
};

export type TopicMetric =
  | "mentions"
  | "weightedMentions"
  | "viewsDriven"
  | "interactionsDriven";

export type EngagementMetric = "views" | "interactions";

export function engagementMetricToTopicMetric(
  metric: EngagementMetric,
): TopicMetric {
  return metric === "interactions" ? "interactionsDriven" : "viewsDriven";
}

export type ProgramTopicGraphNode = {
  id: string;
  slug: string;
  display: string;
  value: number;
  kind: "program" | "topic";
  color?: string;
};

export type ProgramTopicGraphLink = {
  source: string;
  target: string;
  weight: number;
};

export const PROGRAM_TOPIC_OTHERS_SLUG = "__others__";

type AggregateTopicRow = {
  slug: string;
  display: string;
  mentions: number;
  weightedMentions?: number;
  viewsDriven?: number;
  interactionsDriven?: number;
  subtopics?: Array<{
    slug: string;
    display: string;
    mentions: number;
    weightedMentions?: number;
    viewsDriven?: number;
    interactionsDriven?: number;
  }>;
};

type ProgramGraphInput = {
  programId: string | null;
  programName: string;
  topics: AggregateTopicRow[];
};

/** Resolve sizing value from a topic row for the chosen metric. */
export function topicMetricValue(
  row: {
    mentions: number;
    weightedMentions?: number;
    viewsDriven?: number;
    interactionsDriven?: number;
  },
  metric: TopicMetric,
): number {
  if (metric === "interactionsDriven") {
    return row.interactionsDriven ?? row.mentions;
  }
  if (metric === "viewsDriven") {
    return row.viewsDriven ?? row.mentions;
  }
  if (metric === "weightedMentions") {
    return row.weightedMentions ?? row.mentions;
  }
  return row.mentions;
}

/** Sum topic values for a program under the chosen metric. */
export function programTopicTotal(
  program: { topics: Array<{ mentions: number; weightedMentions?: number }> },
  metric: TopicMetric,
): number {
  return program.topics.reduce((sum, topic) => sum + topicMetricValue(topic, metric), 0);
}

/** Stable topic slug → color map from aggregate ranking (shared across programs). */
export function buildTopicColorMap(
  topics: AggregateTopicRow[],
  metric: TopicMetric = "mentions",
): Map<string, string> {
  const sorted = [...topics].sort(
    (a, b) => topicMetricValue(b, metric) - topicMetricValue(a, metric),
  );
  const map = new Map<string, string>();
  sorted.forEach((topic, index) => {
    map.set(
      topic.slug,
      TOPIC_CHART_COLORS[index % TOPIC_CHART_COLORS.length] ?? "#8884d8",
    );
  });
  return map;
}

function topicFillColor(
  slug: string,
  index: number,
  topicColors: string[],
  colorBySlug?: Map<string, string>,
): string {
  return colorBySlug?.get(slug) ?? topicColors[index % topicColors.length] ?? "#8884d8";
}

/** Count topic/subtopic mentions from accepted chunks only. */
export function buildTopicHierarchy(
  data: ContentTaxonomyChunksResponse,
): TopicHierarchyNode[] {
  const topicMap = new Map<
    string,
    {
      display: string;
      mentions: number;
      subtopics: Map<string, { display: string; mentions: number }>;
    }
  >();

  for (const chunk of data.chunks) {
    if (chunk.action !== "accept") continue;
    for (const resolved of chunk.resolved ?? []) {
      const topicSlug = resolved.slug;
      if (!topicSlug) continue;

      let topic = topicMap.get(topicSlug);
      if (!topic) {
        topic = { display: resolved.display, mentions: 0, subtopics: new Map() };
        topicMap.set(topicSlug, topic);
      }
      topic.mentions += 1;
      if (resolved.display) {
        topic.display = resolved.display;
      }

      const subSlug = resolved.subtopic;
      if (!subSlug) continue;

      const subDisplay = resolved.subtopicDisplay ?? subSlug;
      const existing = topic.subtopics.get(subSlug);
      if (existing) {
        existing.mentions += 1;
        if (subDisplay) existing.display = subDisplay;
      } else {
        topic.subtopics.set(subSlug, { display: subDisplay, mentions: 1 });
      }
    }
  }

  return Array.from(topicMap.entries())
    .map(([slug, topic]) => ({
      slug,
      display: topic.display,
      mentions: topic.mentions,
      subtopics: Array.from(topic.subtopics.entries())
        .map(([subSlug, sub]) => ({
          slug: subSlug,
          display: sub.display,
          mentions: sub.mentions,
        }))
        .sort((a, b) => b.mentions - a.mentions),
    }))
    .sort((a, b) => b.mentions - a.mentions);
}

/** Map podcast-level aggregate rows to hierarchy nodes for treemap/network. */
export function buildTopicHierarchyFromAggregate(
  topics: AggregateTopicRow[],
  metric: TopicMetric = "mentions",
): TopicHierarchyNode[] {
  return topics
    .map((topic) => ({
      slug: topic.slug,
      display: topic.display,
      mentions: topicMetricValue(topic, metric),
      subtopics: (topic.subtopics ?? [])
        .map((sub) => ({
          slug: sub.slug,
          display: sub.display,
          mentions: topicMetricValue(sub, metric),
        }))
        .sort((a, b) => b.mentions - a.mentions),
    }))
    .sort((a, b) => b.mentions - a.mentions);
}

function buildSortedTopicDatums(
  hierarchy: TopicHierarchyNode[],
  topicColors: string[],
  colorBySlug?: Map<string, string>,
): TreemapDatum[] {
  return [...hierarchy]
    .sort((a, b) => b.mentions - a.mentions)
    .map((topic, index) => ({
      name: topic.display,
      slug: topic.slug,
      display: topic.display,
      size: topic.mentions,
      kind: "topic" as const,
      fill: topicFillColor(topic.slug, index, topicColors, colorBySlug),
    }));
}

function buildSubtopicCellsForTopic(
  topic: TopicHierarchyNode,
  fill: string,
): TreemapDatum[] {
  const subtopicChildren: TreemapDatum[] = topic.subtopics.map((sub) => ({
    name: sub.display,
    slug: sub.slug,
    display: sub.display,
    size: sub.mentions,
    kind: "subtopic" as const,
    parentTopicSlug: topic.slug,
    parentTopicDisplay: topic.display,
    fill: adjustColor(fill, 0.35),
  }));

  const subtopicMentionSum = subtopicChildren.reduce((sum, c) => sum + c.size, 0);
  const topicOnlyMentions = Math.max(0, topic.mentions - subtopicMentionSum);

  if (topicOnlyMentions > 0) {
    subtopicChildren.push({
      name: `${topic.display} (general)`,
      slug: `${topic.slug}__general`,
      display: `${topic.display} (general)`,
      size: topicOnlyMentions,
      kind: "subtopic",
      parentTopicSlug: topic.slug,
      parentTopicDisplay: topic.display,
      fill: adjustColor(fill, 0.15),
    });
  }

  return subtopicChildren.sort((a, b) => b.size - a.size);
}

export function topicHasDrilldownSubtopics(topic: TopicHierarchyNode): boolean {
  const subtopicMentions = topic.subtopics.reduce((sum, sub) => sum + sub.mentions, 0);
  return topic.subtopics.length > 0 || topic.mentions > subtopicMentions;
}

/** Single-topic subtopic treemap root for drill-down view. */
export function buildDrilldownSubtopicRoot(
  hierarchy: TopicHierarchyNode[],
  topicSlug: string,
  topicColors: string[],
  colorBySlug?: Map<string, string>,
): TreemapDatum | null {
  const sortedTopics = [...hierarchy].sort((a, b) => b.mentions - a.mentions);
  const topicIndex = sortedTopics.findIndex((t) => t.slug === topicSlug);
  const topic = sortedTopics[topicIndex];
  if (!topic || !topicHasDrilldownSubtopics(topic)) return null;

  const fill = topicFillColor(topic.slug, topicIndex, topicColors, colorBySlug);
  const children = buildSubtopicCellsForTopic(topic, fill);
  if (children.length === 0) return null;

  return {
    name: topic.display,
    slug: topic.slug,
    display: topic.display,
    size: topic.mentions,
    kind: "root",
    children,
  };
}

export function hierarchyToTreemapData(
  hierarchy: TopicHierarchyNode[],
  topicColors: string[],
  level: DominanceLevel = "topic",
  colorBySlug?: Map<string, string>,
): TreemapDatum {
  if (level === "subtopic") {
    const children: TreemapDatum[] = buildSortedTopicDatums(hierarchy, topicColors, colorBySlug)
      .map((topicDatum): TreemapDatum | null => {
        const topicNode = hierarchy.find((t) => t.slug === topicDatum.slug);
        if (!topicNode) return null;

        const subtopicChildren = buildSubtopicCellsForTopic(topicNode, topicDatum.fill ?? "#8884d8");
        if (subtopicChildren.length === 0) return null;

        return {
          ...topicDatum,
          children: subtopicChildren.map((sub) => ({
            ...sub,
            fill: adjustColor(
              topicDatum.fill ?? "#8884d8",
              sub.slug.endsWith("__general") ? 0.15 : 0.35,
            ),
          })),
        };
      })
      .filter((topic): topic is TreemapDatum => topic !== null);

    return {
      name: "root",
      slug: "root",
      display: "Episode",
      size: 0,
      kind: "root",
      children,
    };
  }

  return {
    name: "root",
    slug: "root",
    display: "Episode",
    size: 0,
    kind: "root",
    children: buildSortedTopicDatums(hierarchy, topicColors, colorBySlug),
  };
}

/** Bipartite program ↔ topic graph for cross-program comparison. */
export function buildProgramTopicGraph(
  programs: ProgramGraphInput[],
  options: {
    metric?: TopicMetric;
    topTopics?: number;
    colorBySlug?: Map<string, string>;
  } = {},
): { nodes: ProgramTopicGraphNode[]; links: ProgramTopicGraphLink[] } {
  const metric = options.metric ?? "mentions";
  const topN = options.topTopics ?? 12;
  const colorBySlug = options.colorBySlug;

  const topicTotals = new Map<string, { display: string; value: number }>();
  for (const program of programs) {
    for (const topic of program.topics) {
      const value = topicMetricValue(topic, metric);
      if (value <= 0) continue;
      const existing = topicTotals.get(topic.slug);
      if (existing) {
        existing.value += value;
        if (topic.display) existing.display = topic.display;
      } else {
        topicTotals.set(topic.slug, { display: topic.display, value });
      }
    }
  }

  const sortedTopicSlugs = Array.from(topicTotals.entries())
    .sort((a, b) => b[1].value - a[1].value)
    .map(([slug]) => slug);
  const topSlugs = new Set(sortedTopicSlugs.slice(0, topN));

  const bucketSlug = (slug: string): string =>
    topSlugs.has(slug) ? slug : PROGRAM_TOPIC_OTHERS_SLUG;

  const linkWeights = new Map<string, number>();
  for (const program of programs) {
    const programKey = program.programId ?? "unassigned";
    for (const topic of program.topics) {
      const value = topicMetricValue(topic, metric);
      if (value <= 0) continue;
      const targetSlug = bucketSlug(topic.slug);
      const linkKey = `${programKey}::${targetSlug}`;
      linkWeights.set(linkKey, (linkWeights.get(linkKey) ?? 0) + value);
    }
  }

  const topicNodeValues = new Map<string, { display: string; value: number }>();
  Array.from(linkWeights.entries()).forEach(([linkKey, weight]) => {
    const targetSlug = linkKey.split("::")[1]!;
    const display =
      targetSlug === PROGRAM_TOPIC_OTHERS_SLUG
        ? "Others"
        : topicTotals.get(targetSlug)?.display ?? targetSlug;
    const existing = topicNodeValues.get(targetSlug);
    if (existing) {
      existing.value += weight;
    } else {
      topicNodeValues.set(targetSlug, { display, value: weight });
    }
  });

  const programNodes: ProgramTopicGraphNode[] = programs
    .filter((program) => programTopicTotal(program, metric) > 0)
    .map((program) => ({
      id: `program:${program.programId ?? "unassigned"}`,
      slug: program.programId ?? "unassigned",
      display: program.programName,
      value: programTopicTotal(program, metric),
      kind: "program" as const,
    }))
    .sort((a, b) => b.value - a.value);

  const topicNodes: ProgramTopicGraphNode[] = Array.from(topicNodeValues.entries())
    .map(([slug, row]) => ({
      id: `topic:${slug}`,
      slug,
      display: row.display,
      value: row.value,
      kind: "topic" as const,
      color:
        slug === PROGRAM_TOPIC_OTHERS_SLUG
          ? "#9e9e9e"
          : colorBySlug?.get(slug) ?? "#8884d8",
    }))
    .sort((a, b) => b.value - a.value);

  const links: ProgramTopicGraphLink[] = Array.from(linkWeights.entries())
    .filter(([, weight]) => weight > 0)
    .map(([linkKey, weight]) => {
      const [programKey, targetSlug] = linkKey.split("::") as [string, string];
      return {
        source: `program:${programKey}`,
        target: `topic:${targetSlug}`,
        weight,
      };
    });

  return { nodes: [...programNodes, ...topicNodes], links };
}

export function hierarchyToNetworkGraph(
  hierarchy: TopicHierarchyNode[],
  level: DominanceLevel = "topic",
): { nodes: NetworkNode[]; links: NetworkLink[] } {
  const nodes: NetworkNode[] = [];
  const links: NetworkLink[] = [];
  const sortedTopics = [...hierarchy].sort((a, b) => b.mentions - a.mentions);
  const colorIndexBySlug = new Map(sortedTopics.map((topic, index) => [topic.slug, index]));

  if (level === "subtopic") {
    for (const topic of sortedTopics) {
      const topicId = `topic:${topic.slug}`;
      const colorIndex = colorIndexBySlug.get(topic.slug) ?? 0;
      const subtopicMentionSum = topic.subtopics.reduce((sum, sub) => sum + sub.mentions, 0);
      const topicOnlyMentions = Math.max(0, topic.mentions - subtopicMentionSum);
      const hasGroupedSubtopics = topic.subtopics.length > 0 || topicOnlyMentions > 0;

      if (!hasGroupedSubtopics) continue;

      nodes.push({
        id: topicId,
        slug: topic.slug,
        display: topic.display,
        mentions: topic.mentions,
        kind: "topic",
        colorIndex,
      });

      for (const sub of topic.subtopics) {
        const subId = `subtopic:${topic.slug}:${sub.slug}`;
        nodes.push({
          id: subId,
          slug: sub.slug,
          display: sub.display,
          mentions: sub.mentions,
          kind: "subtopic",
          parentTopicSlug: topic.slug,
          colorIndex,
        });
        links.push({ source: topicId, target: subId });
      }

      if (topicOnlyMentions > 0) {
        const generalId = `subtopic:${topic.slug}:__general`;
        nodes.push({
          id: generalId,
          slug: `${topic.slug}__general`,
          display: `${topic.display} (general)`,
          mentions: topicOnlyMentions,
          kind: "subtopic",
          parentTopicSlug: topic.slug,
          colorIndex,
        });
        links.push({ source: topicId, target: generalId });
      }
    }

    return { nodes, links };
  }

  for (const topic of sortedTopics) {
    nodes.push({
      id: `topic:${topic.slug}`,
      slug: topic.slug,
      display: topic.display,
      mentions: topic.mentions,
      kind: "topic",
      colorIndex: colorIndexBySlug.get(topic.slug) ?? 0,
    });
  }

  return { nodes, links };
}

function adjustColor(hex: string, lighten: number): string {
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) return hex;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const mix = (channel: number) =>
    Math.round(channel + (255 - channel) * Math.min(1, Math.max(0, lighten)));
  return `#${mix(r).toString(16).padStart(2, "0")}${mix(g).toString(16).padStart(2, "0")}${mix(b).toString(16).padStart(2, "0")}`;
}

export function hierarchyHasSubtopics(hierarchy: TopicHierarchyNode[]): boolean {
  return hierarchy.some((topic) => topic.subtopics.length > 0);
}

export const TOPIC_CHART_COLORS = [
  "#1976d2",
  "#9c27b0",
  "#2e7d32",
  "#ed6c02",
  "#d32f2f",
  "#0288d1",
  "#7b1fa2",
  "#388e3c",
  "#f57c00",
  "#c2185b",
];
